import { useEffect, useState } from 'react'
import { LOCALES } from '../../config/locales'
import type { CountryCode } from '../../config/locales'
import { LS_COUNTRY, LS_VAT_NEW_CODE, LS_VAT_NEW_PCT, LS_VAT_OVERRIDES, loadVatOverrides } from './constants'

/** Alternatieve BTW-tarieven voor het genereren van willekeurige regels, plus
 * het "nieuw tarief toevoegen"-formulier (newVatCode/newVatPct), waarvan de
 * standaardwaarde meebeweegt met de landkeuze — zie `resetDefaultsForCountry`,
 * aangeroepen door `handleCountryChange` in de hoofdhook. */
export function useVatOverrides() {
  const [vatOverrides, setVatOverrides] = useState<Array<{ code: string; percentage: number }>>(loadVatOverrides)
  const [showVatAdd, setShowVatAdd] = useState(false)
  const [newVatCode, setNewVatCode] = useState(() => localStorage.getItem(LS_VAT_NEW_CODE) ?? 'S')
  const [newVatPct, setNewVatPct] = useState(() => Number(localStorage.getItem(LS_VAT_NEW_PCT) ?? LOCALES[(localStorage.getItem(LS_COUNTRY) as CountryCode) ?? 'NL'].defaultVatPct))

  useEffect(() => {
    try { localStorage.setItem(LS_VAT_OVERRIDES, JSON.stringify(vatOverrides)) } catch { /* quota */ }
  }, [vatOverrides])

  const addVatOverride = () => {
    setVatOverrides(v => [...v, { code: newVatCode, percentage: newVatPct }])
    setNewVatCode('S')
    setNewVatPct(21)
    setShowVatAdd(false)
  }

  const removeVatOverride = (idx: number) =>
    setVatOverrides(v => v.filter((_, i) => i !== idx))

  /** Zet newVatCode/newVatPct terug op het landdefault — aangeroepen wanneer de
   * gebruiker van land wisselt. */
  const resetDefaultsForCountry = (country: CountryCode) => {
    const cfg = LOCALES[country]
    setNewVatCode(cfg.defaultVatCode)
    setNewVatPct(cfg.defaultVatPct)
    localStorage.setItem(LS_VAT_NEW_CODE, cfg.defaultVatCode)
    localStorage.setItem(LS_VAT_NEW_PCT, String(cfg.defaultVatPct))
  }

  return {
    vatOverrides, setVatOverrides,
    showVatAdd, setShowVatAdd,
    newVatCode, setNewVatCode,
    newVatPct, setNewVatPct,
    addVatOverride, removeVatOverride,
    resetDefaultsForCountry,
  }
}

export type VatOverridesState = ReturnType<typeof useVatOverrides>
